import { Loader } from "@ui/Loader";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { VideoPreview } from "@ui/molecules/VideoPreview/VideoPreview";
import {
	useVolumeControl,
	VolumeControl,
} from "@ui/molecules/VideoPreview/VolumeControl";
import { createVideoPreviewOriginatorId } from "@ui/molecules/VideoPreview/videoPreviewVolume";
import { VideoScrubber } from "@ui/molecules/VideoScrubber/VideoScrubber";
import { HStack, VStack } from "@ui/Stack";
import { createEffect, createSignal, Show, useContext } from "solid-js";
import "~/modules/shared/chat/components/VideoPanelContent/VideoCarouselOverlay.css";
import { AddToBoardContext } from "~/modules/shared/boards/boardsContext";

export const VideoPreviewWithScrubber = () => {
	const [store, { setMoveClipTrim }] = useContext(AddToBoardContext);
	const previewURL = () =>
		store.clip?.assets?.find((asset) => asset.type === "preview_mp4");
	const [playing, setPlaying] = createSignal(false);
	const [userPaused, setUserPaused] = createSignal(false);
	const [playbackTime, setPlaybackTime] = createSignal(0);
	const [playbackSeekRequest, setPlaybackSeekRequest] = createSignal<
		{ id: number; clipRelativeSeconds: number } | undefined
	>();
	const [isPortrait, setIsPortrait] = createSignal(false);

	const originatorId = createVideoPreviewOriginatorId();
	const volume = useVolumeControl({
		originatorId,
		getVideoElement: () => undefined,
	});

	const videoStartAbs = () => store.clip?.start ?? 0;
	const absoluteTrimStart = () => store.moveClipTrim.start;
	const absoluteTrimEnd = () => {
		const t = store.moveClipTrim.end;
		if (t !== undefined) {
			return t;
		}
		return store.clip?.clipped_end ?? store.clip?.end ?? videoStartAbs();
	};

	const rangeStartRel = () => absoluteTrimStart() - videoStartAbs();
	const rangeEndRel = () => absoluteTrimEnd() - videoStartAbs();

	const onScrubberPlaybackSeek = (timelineSec: number) => {
		const start = rangeStartRel();
		const end = rangeEndRel();
		const dur = store.clip?.duration;
		const maxT = dur != null && Number.isFinite(dur) && dur > 0 ? dur : end;
		const t = Math.max(0, Math.min(maxT, timelineSec));
		const inTrim = Math.max(start, Math.min(end, t));
		setPlaybackSeekRequest((prev) => ({
			id: (prev?.id ?? 0) + 1,
			clipRelativeSeconds: Math.max(0, inTrim - start),
		}));
	};

	const togglePlay = () => {
		setUserPaused((paused) => !paused);
	};

	createEffect(() => {
		if (!store.clip?.id) return;
		setIsPortrait(false);
		setUserPaused(false);
	});

	const videoWrapperStyle = () =>
		isPortrait()
			? {
					width: "calc(100% * 1 / 3)",
					"max-width": "100%",
					margin: "0 auto",
				}
			: {};
	return (
		<Show
			when={!store.isLoading && store.clip && previewURL()}
			fallback={<Loader />}
		>
			<VStack
				spacing="none"
				fullWidth
				style={{ gap: "0.75rem", "min-width": 0 }}
			>
				<div
					class="move-board-video-preview"
					style={{
						position: "relative",
						...videoWrapperStyle(),
					}}
				>
					<VideoPreview
						portrait={isPortrait()}
						onVideoDimensions={({ width, height }) => {
							setIsPortrait(height > width);
						}}
						startSeconds={rangeStartRel()}
						endSeconds={rangeEndRel()}
						videoUrl={previewURL()?.signed_url ?? ""}
						playTrigger="none"
						showControls={false}
						showDuration={false}
						footerHiddenOn="always"
						forcePause={userPaused()}
						onPlay={() => setPlaying(true)}
						onPause={() => setPlaying(false)}
						onPlaybackTime={setPlaybackTime}
						autoPlay={true}
						loop={true}
						playbackSeekRequest={playbackSeekRequest}
					/>

					<div class="board-video-overlay__content">
						<button
							type="button"
							class="circle-icon large-play-icon board-video-overlay__play-icon"
							aria-label={playing() ? "Pause" : "Play"}
							onClick={(e) => {
								e.stopPropagation();
								togglePlay();
							}}
						>
							<MaterialSymbol
								symbol={playing() ? "pause" : "play_arrow"}
								relativeSize={2}
							/>
						</button>
						<VStack justify="end" style={{ height: "100%" }}>
							<HStack justify="end" fullWidth>
								<VolumeControl controls={volume} />
							</HStack>
						</VStack>
					</div>
				</div>
				<div
					style={{
						width: "100%",
						"min-width": 0,
						overflow: "hidden",
					}}
				>
					<VideoScrubber
						url={previewURL()?.signed_url ?? ""}
						durationSeconds={store.clip.duration}
						sampledFrames={store.clip.sampled_frames ?? undefined}
						rangeStart={rangeStartRel()}
						rangeEnd={rangeEndRel()}
						onRangeChange={({ start: relStart, end: relEnd }) => {
							const base = videoStartAbs();
							setMoveClipTrim({
								start: base + relStart,
								end: base + relEnd,
							});
						}}
						playing={playing}
						playbackPositionSeconds={() => rangeStartRel() + playbackTime()}
						onPlaybackSeek={onScrubberPlaybackSeek}
					/>
				</div>
			</VStack>
		</Show>
	);
};
